import React, { useState, useEffect } from 'react'
import { Copy, Check, Key, Activity } from "lucide-react"
import { Link } from "react-router-dom"
import API from '../../services/api'

export default function MCPLicensingDashboard() {
  const [license, setLicense] = useState(null)
  const [recentScans, setRecentScans] = useState([])
  const [loadingLicense, setLoadingLicense] = useState(false)
  const [loadingScans, setLoadingScans] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)


  const asset_name = sessionStorage.getItem('selectedApp')
  const session = (() => {
    try {
      return JSON.parse(localStorage.getItem('session') || 'null')
    } catch {
      return null
    }
  })()
  const fetchScans = async () => {
    setLoadingScans(true)
    try {
      const res = await API.get('/mcp/scan-files/')
      const mapped = (res.data.files || []).map(f => ({
        id: `${encodeURIComponent(f.asset_name)}/${encodeURIComponent(f.name)}`,
        name: f.name,
        asset_name: f.asset_name,
        date: f.created_at ? new Date(f.created_at).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }).replace(',', '') + ' UTC' : '-',
      }))
      setRecentScans(mapped)
    } catch {
      setRecentScans([])
    } finally {
      setLoadingScans(false)
    }
  }

  useEffect(() => {
    fetchScans()
  }, [])

  const generateLicense = async () => {
    setLoadingLicense(true)
    try {
      const res = await API.post('/mcp/generate-dast-token/', { asset_name,session })
      setLicense(res.data.encrypted_token)
    } finally {
      setLoadingLicense(false)
    }
  }

  const copyLicense = async () => {
    if (!license) return
    try {
      await navigator.clipboard.writeText(license)
      setCopySuccess(true)
    } catch {
      setCopySuccess(false)
    } finally {
      setTimeout(() => setCopySuccess(false), 1200)
    }
  }

  const staticScans = recentScans.filter(s => s.name?.startsWith('Static Scan'))
  const dynamicScans = recentScans.filter(s => s.name?.startsWith('Dynamic Scan'))

  const truncateLicense = (token) => {
    if (!token) return ''
    return token.length > 32 ? token.slice(0, 32) + '...' : token
  }

  return (
    <div className="p-6 sm:p-8 text-white min-h-screen">
      <div className="mb-8">
        <p className="text-white font-bold text-3xl">MCP Scanner</p>
        <p className="text-gray-400 text-sm mt-1">MCP licensing utilities & scan activity dashboard</p>
      </div>

      {/* Generate License — row layout */}
      <div className="bg-[#101012] border border-[#232325] rounded-2xl px-5 py-4 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-white font-semibold shrink-0">
          <Key size={18} />
          Generate License
        </div>
        <p className="text-gray-400 text-sm flex-1 min-w-[180px]">
          Generate a new license key for MCP scanner usage.
        </p>

        {license && (
          <div className="flex items-center gap-2 bg-[#18181b] border border-[#232325] rounded-lg px-3 py-1.5 max-w-xs">
            <code className="text-xs text-gray-300 truncate">{truncateLicense(license)}</code>
            <button
              onClick={copyLicense}
              className="flex items-center gap-1 rounded-md bg-zinc-800 px-2.5 py-1 text-xs font-medium transition-colors hover:bg-zinc-700 shrink-0"
            >
              <span className="relative inline-flex size-4 items-center justify-center">
                <Copy
                  size={14}
                  className={`absolute transition-all duration-200 ${copySuccess ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`}
                />
                <Check
                  size={14}
                  className={`absolute text-green-400 transition-all duration-200 ${copySuccess ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`}
                />
              </span>
              Copy
            </button>
          </div>
        )}

        <button
          onClick={generateLicense}
          disabled={loadingLicense}
          className="shrink-0 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium transition-all hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loadingLicense ? 'Generating...' : 'Generate License'}
        </button>
      </div>

      {/* Dashboard — two scan tables */}
      <div className="bg-[#101012] border border-[#232325] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#232325] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-white font-semibold">
              <Activity size={18} /> Dashboard
            </div>
            <p className="text-gray-400 text-sm mt-0.5">Recent scan activity and issue counts.</p>
          </div>
          <button
            onClick={fetchScans}
            disabled={loadingScans}
            className="rounded-md border border-[#232325] bg-[#18181b] px-3 py-1.5 text-xs font-medium transition-colors hover:bg-[#232325] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingScans ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="p-5 space-y-8">
          <ScanTable title="Static Scans" scans={staticScans} loading={loadingScans} />
          <ScanTable title="Dynamic Scans" scans={dynamicScans} loading={loadingScans} />
        </div>
      </div>
    </div>
  )
}

function ScanTable({ title, scans, loading }) {
  return (
    <div>
      <p className="text-white font-semibold text-base mb-3">{title}</p>
      <div className="bg-[#101012] border border-[#232325] rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">Loading scans…</div>
        ) : scans.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-6">
            <svg className="w-9 h-9 text-gray-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-sm">No scans yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-white min-w-[500px]">
              <thead>
                <tr className="bg-[#232325] text-center">
                  <th className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium whitespace-nowrap">Sl. No</th>
                  <th className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium whitespace-nowrap">Scan Name</th>
                  <th className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium whitespace-nowrap">Time of Scan</th>
                  <th className="px-4 py-3 border-b border-[#232325] text-gray-400 text-sm font-medium whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((scan, index) => (
                  <tr key={index} className="bg-[#101012] hover:bg-[#1a1a1c] transition-colors text-center">
                    <td className="px-4 py-3 border-t border-[#232325] text-gray-400 text-sm">{index + 1}</td>
                    <td className="px-4 py-3 border-t border-[#232325] text-gray-300 text-sm">{scan.name || scan.project || '-'}</td>
                    <td className="px-4 py-3 border-t border-[#232325] text-gray-300 text-sm whitespace-nowrap">{scan.date || scan.time || '-'}</td>
                    <td className="px-4 py-3 border-t border-[#232325]">
                      <Link
                        to={`/mcp-scan/${scan.id}`}
                        className="bg-blue-500/10 hover:bg-blue-600/20 px-2.5 py-0.5 rounded-full text-xs font-medium text-blue-400 border border-blue-500/20 transition-colors"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
